import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Search, Plus, Trash2 } from "lucide-react";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";

export default function BlockCar() {
  const { vehicleId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isKec = user.role === "sales";
  const [type, setType] = useState(params.get("type") === "home" ? "home" : "showroom");
  const [vehicles, setVehicles] = useState([]);
  const [kecs, setKecs] = useState([]);
  const [vehicle, setVehicle] = useState(vehicleId || "");
  const [kec, setKec] = useState("");
  const [lookup, setLookup] = useState("");
  const [enquiry, setEnquiry] = useState(null);
  const [lookupErr, setLookupErr] = useState("");
  const [address, setAddress] = useState("");
  const [expectedKm, setExpectedKm] = useState("");
  const [slot, setSlot] = useState("");
  const [when, setWhen] = useState("now");
  const [purpose, setPurpose] = useState("fuel");
  const [note, setNote] = useState("");
  const [hours, setHours] = useState("2");
  const [customers, setCustomers] = useState([]);
  const [coKec, setCoKec] = useState("");
  const [driverName, setDriverName] = useState("");
  const [destination, setDestination] = useState("");
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data.filter((v) => v.status === "available" || v.id === vehicleId)));
    api.get("/kec-roster").then((r) => setKecs(r.data.filter((k) => k.active)));
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    if (isKec) setKec(user.id);
  }, [vehicleId]);

  const doLookup = async () => {
    setLookupErr(""); setEnquiry(null);
    const isEnq = lookup.toUpperCase().startsWith("ENQ");
    try {
      const { data } = await api.get("/salesforce/lookup", { params: isEnq ? { enquiry_id: lookup } : { phone: lookup } });
      setEnquiry(data);
    } catch (e) { setLookupErr(errMsg(e)); }
  };

  const addCustomer = () => {
    const custKec = isKec ? (coKec || user.id) : kec;
    if (!enquiry || !custKec) { toast.error("Look up the enquiry and pick this customer's KEC first"); return; }
    if (customers.some((c) => c.enquiry_id === enquiry.enquiry_id)) { toast.error("Customer already added"); return; }
    if (customers.length >= 5) { toast.error("Maximum 5 customers per connected drive"); return; }
    setCustomers([...customers, { enquiry_id: enquiry.enquiry_id, customer_name: enquiry.customer_name, kec_roster_id: custKec, kec_name: custKec === user.id ? user.name : kecs.find((k) => k.id === custKec)?.kec_name }]);
    setEnquiry(null); setLookup("");
  };

  const submit = async () => {
    if (!vehicle) { toast.error("Select a vehicle"); return; }
    setBusy(true);
    try {
      const body = { vehicle_id: vehicle, type };
      if (type === "utility") {
        Object.assign(body, { kec_roster_id: kec || null, driver_name: driverName.trim() || null, utility_purpose: purpose, utility_note: note, utility_destination: destination.trim() || null, expected_hours: parseFloat(hours) || 2 });
        if (isKec && !kec) throw { response: { data: { detail: "Select the driver/KEC" } } };
      } else if (type === "connected") {
        if (customers.length < 2) throw { response: { data: { detail: "Add at least 2 customers" } } };
        Object.assign(body, { customers, address, expected_km: parseFloat(expectedKm) || null, slot_start: slot ? new Date(slot).toISOString() : null });
      } else {
        if (!kec || !enquiry) throw { response: { data: { detail: "Select KEC and a valid Salesforce enquiry" } } };
        if (type === "showroom" && when === "later" && !slot) throw { response: { data: { detail: "Pick the date & time for the advance block" } } };
        const useSlot = type === "home" || when === "later";
        Object.assign(body, { kec_roster_id: kec, enquiry_id: enquiry.enquiry_id, address: address || null, expected_km: expectedKm ? parseFloat(expectedKm) : null, slot_start: useSlot && slot ? new Date(slot).toISOString() : null });
      }
      const { data } = await api.post("/bookings", body);
      toast.success(data.status === "pending_sm_approval" ? (data.advance ? "Reservation sent — awaiting SM approval" : "Request sent — awaiting SM approval") : data.status === "approved" ? `Reserved — ${data.reg_no} is held for your slot` : `Approved — ${data.reg_no} blocked`);
      nav(data.status === "blocked" ? "/trips" : "/board");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const kecSelect = (testId) => isKec ? (
    <div data-testid={testId} className="mt-1 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">Attributed to you — {user.name}</div>
  ) : (
    <select data-testid={testId} value={kec} onChange={(e) => setKec(e.target.value)} className={inputCls}>
      <option value="">Select KEC…</option>
      {kecs.map((k) => <option key={k.id} value={k.id}>{k.kec_name}</option>)}
    </select>
  );

  const selVehicle = vehicles.find((v) => v.id === vehicle);
  const additionalModel = enquiry && selVehicle && enquiry.model_of_interest && enquiry.model_of_interest !== selVehicle.model;

  const sfLookupBlock = (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Salesforce Enquiry ID or Customer Phone</label>
      <div className="mt-1 flex gap-2">
        <input data-testid="sf-lookup-input" value={lookup} onChange={(e) => setLookup(e.target.value)} placeholder="ENQ-20261000 or 9847012345"
          className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]" />
        <button data-testid="sf-lookup-btn" onClick={doLookup} className="rounded-xl bg-[#0E2240] px-4 text-white active:scale-95 transition-transform duration-150"><Search size={17} /></button>
      </div>
      {enquiry && (
        <div data-testid="sf-enquiry-result" className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm">
          <div className="font-bold text-emerald-800">{enquiry.customer_name}</div>
          <div className="text-xs text-emerald-700">{[enquiry.enquiry_id, enquiry.customer_phone, enquiry.model_of_interest, enquiry.enquiry_status].filter(Boolean).join(" · ")}</div>
        </div>
      )}
      {additionalModel && (
        <div data-testid="additional-model-note" className="mt-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs font-medium text-amber-800">
          Enquiry is for <b>{enquiry.model_of_interest}</b>. You are booking a <b>{selVehicle.model}</b> — this will be recorded as an <b>additional model drive</b> on the same enquiry.
        </div>
      )}
      {lookupErr && <div data-testid="sf-lookup-error" className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-medium text-[#BB162B]">{lookupErr}</div>}
    </div>
  );

  return (
    <div className="px-4 py-5 lg:px-8 max-w-xl">
      <button data-testid="back-btn" onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500"><ArrowLeft size={15} /> Back</button>
      <h1 className="mt-2 font-heading text-2xl font-bold text-[#0E2240]">Block a Car</h1>
      <p className="text-xs text-slate-500 mt-0.5">Every request goes to the Sales Manager for one-tap approval</p>

      <div className="mt-4 grid grid-cols-4 rounded-xl bg-slate-100 p-1">
        {[["showroom", "Showroom"], ["home", "Home"], ["connected", "Connected"], ["utility", "Utility"]].map(([t, l]) => (
          <button key={t} data-testid={`td-type-${t}`} onClick={() => setType(t)}
            className={`rounded-lg py-2.5 text-xs font-bold transition-colors ${type === t ? "bg-[#0E2240] text-white" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-4 space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle</label>
          <select data-testid="vehicle-select" value={vehicle} onChange={(e) => setVehicle(e.target.value)} className={inputCls}>
            <option value="">Select vehicle…</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.reg_no} — {v.model} {v.variant}</option>)}
          </select>
        </div>

        {(type === "showroom" || type === "home") && (
          <>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">KEC (mandatory attribution)</label>{kecSelect("kec-select")}</div>
            {sfLookupBlock}
          </>
        )}

        {type === "showroom" && (
          <div data-testid="when-toggle" className="rounded-2xl border border-slate-200 bg-white p-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">When</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {[["now", "Live — drive now"], ["later", "Block for later"]].map(([w, l]) => (
                <button key={w} data-testid={`when-${w}`} onClick={() => setWhen(w)}
                  className={`rounded-xl border py-2.5 text-xs font-bold ${when === w ? "border-[#0E2240] bg-[#0E2240] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{l}</button>
              ))}
            </div>
            {when === "later" && (
              <div className="mt-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date & time (up to 7 days ahead)</label>
                <input data-testid="advance-slot-input" type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)}
                  min={new Date(Date.now() + 10 * 60000).toISOString().slice(0, 16)} max={new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16)} className={inputCls} />
                <p className="mt-1 text-[10px] text-slate-400">The car stays available for live drives until your slot; it is held for 1 hour from the slot time after SM approval.</p>
              </div>
            )}
          </div>
        )}
        {type === "home" && (
          <>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer Address</label>
              <textarea data-testid="home-address-input" value={address} onChange={(e) => setAddress(e.target.value)} rows={2} placeholder="Full address" className={inputCls} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Expected round-trip km</label>
                <input data-testid="home-expected-km-input" type="number" value={expectedKm} onChange={(e) => setExpectedKm(e.target.value)} placeholder="20" className={inputCls} /></div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date & slot</label>
                <input data-testid="home-slot-input" type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} className={inputCls} /></div>
            </div>
          </>
        )}

        {type === "connected" && (
          <>
            <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 text-[11px] text-purple-800">One outing, one gate pass — 2–5 customers each validated in Salesforce and attributed to a KEC.</div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">This customer's KEC</label>
              {isKec ? (
                <select data-testid="connected-kec-select" value={coKec || user.id} onChange={(e) => setCoKec(e.target.value)} className={inputCls}>
                  <option value={user.id}>Me — {user.name}</option>
                  {kecs.filter((k) => k.id !== user.id).map((k) => <option key={k.id} value={k.id}>{k.kec_name} (co-KEC travelling with me)</option>)}
                </select>
              ) : kecSelect("connected-kec-select")}
              {isKec && <p className="mt-1 text-[10px] text-slate-400">Two KECs going out together? Pick your colleague for their customer — both of you can work the trip.</p>}</div>
            {sfLookupBlock}
            <button data-testid="add-customer-btn" onClick={addCustomer} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 py-2.5 text-sm font-bold text-slate-600"><Plus size={15} /> Add customer to drive</button>
            {customers.length > 0 && (
              <div className="space-y-1.5">
                {customers.map((c, i) => (
                  <div key={c.enquiry_id} className="flex items-center justify-between rounded-lg bg-slate-50 p-2.5 text-xs">
                    <span><b className="text-[#0E2240]">{i + 1}. {c.customer_name}</b> · {c.enquiry_id} · KEC {c.kec_name}</span>
                    <button onClick={() => setCustomers(customers.filter((x) => x.enquiry_id !== c.enquiry_id))} className="text-slate-400"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Destination area</label>
                <input data-testid="connected-destination-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. Muvattupuzha" className={inputCls} /></div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Expected total km</label>
                <input data-testid="connected-expected-km-input" type="number" value={expectedKm} onChange={(e) => setExpectedKm(e.target.value)} placeholder="80" className={inputCls} /></div>
            </div>
          </>
        )}

        {type === "utility" && (
          <>
            <div className="rounded-xl border border-slate-300 bg-slate-100 p-3 text-[11px] text-slate-600">Non-customer movement (no Salesforce enquiry) — still SM-approved with a gate pass and odometer control.</div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Purpose</label>
              <select data-testid="utility-purpose-select" value={purpose} onChange={(e) => setPurpose(e.target.value)} className={inputCls}>
                {[["fuel", "Fuel refill"], ["workshop", "Workshop movement"], ["rto", "RTO work"], ["cleaning", "Cleaning / Detailing"], ["delivery_pickup", "Picking up delivery customer"], ["marketing", "Marketing / event display"], ["other", "Other"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Destination (optional)</label>
              <input data-testid="utility-destination-input" list="utility-dest-list" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g. Service centre Kalamassery / another branch / fuel pump" className={inputCls} />
              <datalist id="utility-dest-list">{branches.filter((b) => b.id !== user.branch_id).map((b) => <option key={b.id} value={b.name} />)}</datalist>
              <p className="mt-1 text-[10px] text-slate-400">Shown to Security on the pass and the Out-Now list. Gate OUT and IN are both recorded at your own gate.</p></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Who is taking the vehicle?</label>
              {isKec ? kecSelect("utility-kec-select") : (
                <select data-testid="utility-kec-select" value={kec} onChange={(e) => setKec(e.target.value)} className={inputCls}>
                  <option value="">Myself — {user.name} ({user.role.toUpperCase()})</option>
                  {kecs.map((k) => <option key={k.id} value={k.id}>KEC · {k.kec_name}</option>)}
                </select>
              )}
              {!isKec && <p className="mt-1 text-[10px] text-slate-400">Leave as “Myself” when you coordinate the run yourself (e.g. washing, fuel). Pick a KEC only if a KEC is driving.</p>}</div>
            {!isKec && !kec && (
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Outside person driving (optional)</label>
                <input data-testid="utility-driver-name-input" value={driverName} onChange={(e) => setDriverName(e.target.value)} placeholder="e.g. Driver Shaji / Marketing agency rep — leave blank if it's you" className={inputCls} />
                {user.role === "sm" && <p className="mt-1 text-[10px] text-emerald-700">Raised by the SM — approved on the spot, TDC informed.</p>}</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Expected duration (hrs)</label>
                <input data-testid="utility-hours-input" type="number" value={hours} onChange={(e) => setHours(e.target.value)} className={inputCls} /></div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Note</label>
                <input data-testid="utility-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className={inputCls} /></div>
            </div>
          </>
        )}

        <button data-testid="submit-block-btn" disabled={busy} onClick={submit}
          className="w-full rounded-xl bg-[#BB162B] py-3.5 text-sm font-bold text-white active:scale-[0.98] transition-transform duration-150 disabled:opacity-60">
          {busy ? "Submitting…" : "Send for SM Approval"}
        </button>
      </div>
    </div>
  );
}
